import { useState, useEffect, useRef } from 'react';

/**
 * Smooth streaming hook for animating text character-by-character
 *
 * Decouples network streaming (fast) from visual streaming (smooth).
 * Uses requestAnimationFrame for consistent 60fps animation.
 *
 * The RAF loop reads text from a ref (not a closure), so it stays alive
 * across chunk arrivals without restarting — critical for providers that
 * send small chunks (e.g. OpenAI GPT ~2-3 chars).
 *
 * @param {string} text - The full content to animate (fallback when bufferRef not provided)
 * @param {boolean} isStreaming - Whether content is still being streamed
 * @param {number} speed - Base animation speed in ms per character
 * @param {Function} onAnimationComplete - Optional callback when animation finishes
 * @param {boolean} forceComplete - Freeze animation at current position (stop button)
 * @param {{ current: string }} bufferRef - Optional shared ref written by SSE handler, read by RAF loop
 * @returns {string} - The currently visible (animated) content
 */
export const useSmoothStream = (text, isStreaming, speed = 8, onAnimationComplete, forceComplete = false, bufferRef, visibleLengthRef) => {
  const [visibleContent, setVisibleContent] = useState('');
  const animationFrameRef = useRef(null);
  const targetIndexRef = useRef(0);
  const previousTextRef = useRef('');
  const hasAnimatedRef = useRef(false);
  const isInitializedRef = useRef(false);
  const isFrozenRef = useRef(false);
  const lastFrameTimeRef = useRef(null);

  // Refs for latest values — the RAF loop reads these instead of closed-over props,
  // so the animation effect doesn't need text/isStreaming in its dependency array.
  const textRef = useRef(text);
  // eslint-disable-next-line react-hooks/refs -- latest-value ref read by the RAF loop on purpose; an effect sync would lag a frame and stutter the stream
  textRef.current = text;

  const isStreamingRef = useRef(isStreaming);
  // eslint-disable-next-line react-hooks/refs -- latest-value ref: the animation effect reads isStreaming per frame without re-subscribing
  isStreamingRef.current = isStreaming;

  // Initialize content on first render - show instantly if not streaming
  useEffect(() => {
    // First time initialization with existing content - show immediately
    if (!isInitializedRef.current && text && !isStreaming) {
      setVisibleContent(text);
      previousTextRef.current = text;
      targetIndexRef.current = text.length;
      hasAnimatedRef.current = true;
      isInitializedRef.current = true;
      return;
    }

    // Mark as initialized even if empty
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
    }
  }, [text, isStreaming]);

  // Reset animation when text changes significantly
  useEffect(() => {
    // Skip on first render (handled by initialization effect)
    if (!isInitializedRef.current) return;

    // If text is completely new (not just appended), reset animation
    if (text !== previousTextRef.current && !text.startsWith(previousTextRef.current)) {
      setVisibleContent('');
      targetIndexRef.current = 0;
      previousTextRef.current = text;
      hasAnimatedRef.current = false;
      lastFrameTimeRef.current = null;
    } else {
      previousTextRef.current = text;
    }
  }, [text]);

  // Force-complete: freeze at current animation position (used by stop button)
  useEffect(() => {
    if (!forceComplete) return;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    isFrozenRef.current = true;
    hasAnimatedRef.current = true;
    // Don't change visibleContent — keep it wherever the animation reached
  }, [forceComplete]);

  // Main animation loop — depends only on isStreaming and speed, NOT text.
  // The RAF loop reads textRef.current each frame, so it picks up new chunks
  // without being cancelled and restarted (which caused jank with small chunks).
  useEffect(() => {
    // Don't override if frozen by stop button
    if (isFrozenRef.current) return;

    const currentText = textRef.current;

    // If not streaming and nothing to animate, show empty
    if (!isStreaming && currentText.length === 0) {
      setVisibleContent('');
      return;
    }

    // If not streaming and animation already caught up, show full text and notify
    if (!isStreaming && hasAnimatedRef.current && targetIndexRef.current >= currentText.length) {
      setVisibleContent(currentText);
      if (onAnimationComplete) {
        onAnimationComplete();
      }
      return;
    }

    // Fresh stream — reset frame timer so animation starts clean
    if (isStreaming) {
      lastFrameTimeRef.current = null;
    }

    const animate = (timestamp) => {
      if (lastFrameTimeRef.current === null) {
        lastFrameTimeRef.current = timestamp;
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      const deltaTime = timestamp - lastFrameTimeRef.current;
      lastFrameTimeRef.current = timestamp;

      // Read from shared buffer ref (SSE writes directly) when available, else text prop
      const latestText = bufferRef?.current != null ? bufferRef.current : textRef.current;

      if (targetIndexRef.current < latestText.length) {
        const charsThisFrame = Math.max(1, Math.floor(deltaTime / speed));

        targetIndexRef.current = Math.min(
          targetIndexRef.current + charsThisFrame,
          latestText.length
        );
        setVisibleContent(latestText.slice(0, targetIndexRef.current));
        if (visibleLengthRef) visibleLengthRef.current = targetIndexRef.current;
      }

      // Keep loop alive while streaming (idle-spin when caught up, pick up next chunk).
      // After streaming ends, continue until remaining buffer is drained.
      const fullText = bufferRef?.current != null ? bufferRef.current : textRef.current;
      if (isStreamingRef.current || targetIndexRef.current < fullText.length) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Streaming done and animation caught up — finalize
        hasAnimatedRef.current = true;
        setVisibleContent(fullText);
        if (visibleLengthRef) visibleLengthRef.current = fullText.length;
        if (onAnimationComplete) {
          onAnimationComplete();
        }
      }
    };

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isStreaming, speed]);

  return visibleContent;
};
